/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }

==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import config from '@/config'
import {isUserLogged}  from './auth-service';


Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
  routes: [

    // =============================================================================
    // MAIN BGT ROUTES FULL WIDTH FULL WIDTH
    // =============================================================================

    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        {
          path: '/',
          redirect: '/login'
        },
        {
          path: '/login',
          name: 'login',
          component: () => import('@/views/pages/login/Login.vue'),
          meta: {
            rule: 'isPublic'
          }
        },
        {
          path: '/forget-password',
          name: 'forget-password',
          component: () => import('@/views/pages/login/RecoverPassword.vue'),
          meta: {
            rule: 'isPublic'
          }
        },
      ]
    },
    // =============================================================================
    // END - MAIN BRT ROUTES FULL WIDTH
    // =============================================================================


    // =============================================================================
    // MAIN ROUTES INSIDE USER
    // =============================================================================
    {
      path: '/user',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        {
          path: 'dashboard',
          name: 'user-dashboard',
          component: () => import('@/views/UserDashboard.vue'),
          meta: {
            rule: 'isUser',
            authRequired:true
          }
        },
        {
          path: 'my-profile',
          name: 'app-user-edit',
          component: () => import('@/views/pages/common/profile-management/UserEdit.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'User Settings',  active: true}
            ],
            pageTitle: 'User Profile',
            rule: 'isUser',
            authRequired:true
          }
        },
        {
          path: 'manage-service-units',
          name: 'manage-service-units',
          component: () => import('@/views/pages/user/service-unit-management/ServiceUnitList.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Service Unit Management',  active: true}
            ],
            pageTitle: 'Service Unit Management',
            rule: 'isUser',
            authRequired:true
          }
        },
        {
          path: 'manage-process',
          name: 'manage-process',
          component: () => import('@/views/pages/user/service-unit-process/ProcessList.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Service Unit Process',  active: true}
            ],
            pageTitle: 'Service Unit Process',
            rule: 'isUser',
            authRequired:true
          }
        },
        {
          path: 'manage-screenshots',
          name: 'manage-screenshots',
          component: () => import('@/views/pages/user/service-unit-screenshots/ScreenshotsList.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Service Unit Screenshots',  active: true}
            ],
            pageTitle: 'Service Unit Screenshots',
            rule: 'isUser',
            authRequired:true
          }
        },
        {
          path: 'url-restrictions',
          name: 'url-restrictions',
          component: () => import('@/views/pages/user/service-unit-url-restriction/UrlRestrictionList.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Url Restrictions',  active: true}
            ],
            pageTitle: 'Url Restrictions',
            rule: 'isUser',
            authRequired:true
          }
        },
      ]
    },
    // =============================================================================
    // END - MAIN ROUTES INSIDE USER
    // =============================================================================


    // =============================================================================
    // MAIN ROUTES INSIDE ADMIN
    // =============================================================================
    {
      path: '/admin',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        {
          path: 'dashboard',
          name: 'admin-dashboard',
          component: () => import('@/views/AdminDashboard.vue'),
          meta: {
            rule: 'isAdmin',
            authRequired:true
          }
        },
        {
          path: 'my-profile',
          name: 'app-user-edit',
          component: () => import('@/views/pages/common/profile-management/UserEdit.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'User Settings',  active: true}
            ],
            pageTitle: 'User Profile',
            rule: 'isAdmin',
            authRequired:true
          }
        },
        {
          path: 'manage-organizations',
          name: 'manage-organizations',
          component: () => import('@/views/pages/admin/organization-management/OrganizationList.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Organization Management', active: true},
            ],
            pageTitle: 'Organization Management',
            rule: 'isAdmin',
            authRequired:true
          }
        },
        {
          path: 'manage-users',
          name: 'manage-users',
          component: () => import('@/views/pages/admin/user-management/UserList.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'User Management', active: true},
            ],
            pageTitle: 'User Management',
            rule: 'isAdmin',
            authRequired:true
          }
        },
        {
          path: 'reporting/prediction',
          name: 'prediction-report',
          component: () => import('@/views/pages/admin/report-management/PredictionReport.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Report', url: '/'},
              {title: 'Prediction', active: true},
            ],
            pageTitle: 'Prediction Report',
            rule: 'isAdmin',
            authRequired:true
          }
        },
      ]
    },
    // =============================================================================
    // END - MAIN ROUTES INSIDE ADMIN
    // =============================================================================


    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/callback',
          name: 'auth-callback',
          component: () => import('@/views/Callback.vue'),
          meta: {
            rule: 'isPublic'
          }
        },


        {
          path: '/pages/lock-screen',
          name: 'page-lock-screen',
          component: () => import('@/views/pages/LockScreen.vue'),
          meta: {
            rule: 'isPublic'
          }
        },
        {
          path: '/pages/comingsoon',
          name: 'page-coming-soon',
          component: () => import('@/views/pages/ComingSoon.vue'),
          meta: {
            rule: 'isPublic'
          }
        },
        {
          path: '/pages/error-404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue'),
          meta: {
            rule: 'isPublic'
          }
        },
        {
          path: '/pages/error-500',
          name: 'page-error-500',
          component: () => import('@/views/pages/Error500.vue'),
          meta: {
            rule: 'isPublic'
          }
        },
        {
          path: '/pages/not-authorized',
          name: 'page-not-authorized',
          component: () => import('@/views/pages/NotAuthorized.vue'),
          meta: {
            rule: 'isPublic'
          }
        },
        {
          path: '/pages/maintenance',
          name: 'page-maintenance',
          component: () => import('@/views/pages/Maintenance.vue'),
          meta: {
            rule: 'isPublic'
          }
        }
      ]
    },


    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/error-404'
    }
  ]


})


router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')

  if (appLoading) {
    appLoading.style.display = 'none'
  }


})

router.beforeEach((to, from, next) => {
  console.log(to);
    if (
      (to.path === "/login" ||
        to.path === "/forget-password" ||
        to.path === "/error-404" ||
        to.path === "/error-500" ||
        to.path === "/register" ||
        to.path === "/callback" ||
        to.path === "/comingsoon")
        &&    (!isUserLogged())
    ) {
        return next();
    }

  if ((to.path === "/login") && isUserLogged()) {
    const userInfo = JSON.parse(localStorage.getItem('auth-values'))
    if(config.USER_ROLE_ALL_ADMIN.includes(userInfo.user_default_role_id)){
      router.push({ path: '/admin/dashboard', query: { to: to.path } })
    }else{
      router.push({ path: '/user/dashboard', query: { to: to.path } })
    }
  }

  // If auth required, check login. If login fails redirect to login page
  if (to.meta.authRequired) {
      if (!(isUserLogged())) {
        router.push({ path: '/login', query: { to: to.path } })
      }
  }

  return next()
})

export default router
